/* =============================================================================
 * Button — APS primitive (Track D)
 * -----------------------------------------------------------------------------
 * Token-driven button replacing the 677 hand-styled <button>s. Variants match
 * the design canvas: primary (orange), secondary (outline), ghost, danger.
 * Focus ring comes from the global :focus-visible token rule; hover/active from
 * the `.aps-btn` rule (styles/globals.css) — inline styles can't express :hover.
 * ========================================================================== */
import React from 'react';
import { radius, font } from '../../styles/tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const SIZES: Record<ButtonSize, { padV: number; padH: number; fs: number; gap: number; r: number }> = {
  sm: { padV: 8, padH: 14, fs: 13, gap: 6, r: radius.md },   // 10px — spec dominant
  md: { padV: 11, padH: 18, fs: 14, gap: 8, r: radius.md },  // 10px
  lg: { padV: 14, padH: 22, fs: 15, gap: 9, r: radius.lg },  // 12px — full-width CTA
};

function variantStyle(variant: ButtonVariant): React.CSSProperties {
  switch (variant) {
    case 'secondary':
      return { background: 'var(--aps-surface)', color: 'var(--aps-ink)', border: '1.5px solid var(--aps-border-strong)' };
    case 'ghost':
      return { background: 'transparent', color: 'var(--aps-text-2)', border: '1.5px solid transparent' };
    case 'danger':
      return { background: 'var(--aps-danger)', color: 'var(--aps-on-ink)', border: '1.5px solid transparent' };
    case 'primary':
    default:
      return { background: 'var(--aps-action)', color: 'var(--aps-on-ink)', border: '1.5px solid transparent' };
  }
}

const Spinner: React.FC<{ size: number }> = ({ size }) => (
  <span
    aria-hidden
    style={{
      width: size, height: size, borderRadius: radius.pill,
      border: '2px solid currentColor', borderTopColor: 'transparent',
      display: 'inline-block', animation: 'spin 0.7s linear infinite',
    }}
  />
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = 'primary', size = 'md', fullWidth, loading, leftIcon, rightIcon, disabled, children, className, style, ...rest },
    ref
  ) => {
    const s = SIZES[size];
    const isDisabled = disabled || loading;
    const isGhost = variant === 'ghost';
    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={['aps-btn', className].filter(Boolean).join(' ')}
        style={{
          display: fullWidth ? 'flex' : 'inline-flex',
          width: fullWidth ? '100%' : undefined,
          alignItems: 'center',
          justifyContent: 'center',
          gap: s.gap,
          fontFamily: font.sans,
          fontSize: s.fs,
          fontWeight: isGhost ? font.weight.semibold : font.weight.bold, // ghost = 600 (spec)
          lineHeight: 1,
          padding: isGhost ? `${s.padV}px 8px` : `${s.padV}px ${s.padH}px`, // ghost = tighter (spec)
          borderRadius: s.r,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.55 : 1,
          transition: 'filter var(--aps-dur-fast) var(--aps-ease-out), transform var(--aps-dur-fast) var(--aps-ease-out)',
          boxSizing: 'border-box',
          ...variantStyle(variant),
          ...style,
        }}
        {...rest}
      >
        {loading ? <Spinner size={s.fs} /> : leftIcon}
        {children != null && <span>{children}</span>}
        {!loading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
export default Button;
